import { ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Lead, Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import MoreMenu from '../ui/MoreMenu';
import { EmptyState, ErrorNotice, InfoNotice, SkeletonRows } from '../ui/States';
import { errorMessage, inputClass } from '../../lib/programs';
import { openBlankTab, showPrintable } from '../../lib/printable';
import { SendSteps } from './SendSteps';
import ChangesTable, { sentNote } from './ChangesTable';
import AddLink from '../ui/AddLink';
import {
  CateringPerson,
  CateringSettings,
  CateringView,
  DayPlan,
  Dinner,
  RunSheetLine,
  em,
  headRow,
  monthDay,
  num,
  td,
  th,
  weekDay,
  when,
} from './logistics';

const FIELD_LABELS: Record<string, string> = { days: 'days', dietary: 'dietary', dinner: 'dinner', count: 'people' };
const PEOPLE_SHOWN = 10;

// Tables that stack on a phone: one block per row, each cell on its own line with its column name.
const stackTable = 'w-full tabular max-md:block';
const stackRow = 'border-b border-line max-md:block max-md:py-3';
const stackCell = `${td} max-md:block max-md:py-[2px] max-md:pr-0`;
function Col({ children }: { children: ReactNode }) {
  return <span className="md:hidden text-small text-ink-3">{children} · </span>;
}
const check = 'w-[18px] h-[18px] accent-accent';
const fieldLabel = 'block text-[15px] leading-5 font-semibold text-ink mb-[6px]';
const smallInput = 'border border-line-strong rounded-sm bg-white px-2 min-h-[36px] text-[15px] text-ink';
const dash = <span className="text-ink-3">–</span>;

function sentence(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
}
function list(items: string[]) {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function MealsModal({
  eventId,
  view,
  onClose,
  onSaved,
}: {
  eventId: string;
  view: CateringView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [meals, setMeals] = useState<Record<string, string[]>>(
    Object.fromEntries(view.days.map((d) => [d.date, d.meals])),
  );
  const save = useMutation(async () => (await api.put(`/events/${eventId}/catering/settings`, { meals })).data.data, {
    onSuccess: onSaved,
  });
  const toggle = (date: string, key: string) =>
    setMeals((m) => ({ ...m, [date]: m[date].includes(key) ? m[date].filter((x) => x !== key) : [...m[date], key] }));
  return (
    <Modal
      title="Change meals"
      why="Tick the meals served each day. The numbers follow the people attending in person that day."
      onClose={onClose}
      width={720}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Save meals
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      {save.error ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full tabular">
          <thead>
            <tr className={headRow}>
              <th className={th}>Day</th>
              {view.meals.map((m) => (
                <th key={m.key} className={`${th} text-center`}>
                  {m.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.days.map((d) => (
              <tr key={d.date} className="border-b border-line">
                <td className={`${td} whitespace-nowrap`}>{weekDay(d.date)}</td>
                {view.meals.map((m) => (
                  <td key={m.key} className={`${td} text-center`}>
                    <input
                      type="checkbox"
                      className={check}
                      aria-label={`${m.label} on ${weekDay(d.date)}`}
                      checked={meals[d.date]?.includes(m.key) ?? false}
                      onChange={() => toggle(d.date, m.key)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function AudiencesModal({
  eventId,
  view,
  onClose,
  onSaved,
}: {
  eventId: string;
  view: CateringView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [st, setSt] = useState<Pick<CateringSettings, 'mealAudiences' | 'extra'>>({
    mealAudiences: view.settings.mealAudiences,
    extra: view.settings.extra,
  });
  const [choices, setChoices] = useState<Record<string, string>>(
    Object.fromEntries(
      view.days
        .filter((d) => d.meals.includes('dinner'))
        .map((d) => [d.date, (view.settings.dinnerChoices[d.date] ?? []).join(', ')]),
    ),
  );
  const save = useMutation(
    async () =>
      (
        await api.put(`/events/${eventId}/catering/settings`, {
          ...st,
          dinnerChoices: Object.fromEntries(
            Object.entries(choices).map(([date, t]) => [
              date,
              t
                .split(',')
                .map((x) => x.trim())
                .filter(Boolean),
            ]),
          ),
        })
      ).data.data,
    { onSuccess: onSaved },
  );
  const aud = (date: string, meal: string) => st.mealAudiences[date]?.[meal] ?? [];
  const toggle = (date: string, meal: string, key: string) =>
    setSt((x) => {
      const cur = aud(date, meal);
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      return {
        ...x,
        mealAudiences: { ...x.mealAudiences, [date]: { ...(x.mealAudiences[date] ?? {}), [meal]: next } },
      };
    });
  const setExtra = (date: string, meal: string, field: 'count' | 'note', value: string) =>
    setSt((x) => {
      const cur = x.extra[date]?.[meal] ?? { count: 0, note: null };
      const next = field === 'count' ? { ...cur, count: Number(value) || 0 } : { ...cur, note: value || null };
      return { ...x, extra: { ...x.extra, [date]: { ...(x.extra[date] ?? {}), [meal]: next } } };
    });
  const mealName = (m: string) => view.meals.find((x) => x.key === m)?.label ?? m;
  return (
    <Modal
      title="Who eats what, extra headcounts"
      why="Leave every audience unticked for a meal everyone eats. Add a partner’s headcount (or anyone not on the list) as extra."
      onClose={onClose}
      width={880}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      {save.error ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
      <div className="space-y-8">
        {view.days.map((d) => (
          <div key={d.date}>
            <h3 className="text-[15px] leading-[22px] font-semibold text-ink mb-2">{weekDay(d.date)}</h3>
            <div className="overflow-x-auto">
              <table className="w-full tabular min-w-[620px]">
                <thead>
                  <tr className={headRow}>
                    <th className={th}>Meal</th>
                    <th className={th}>Who eats it</th>
                    <th className={th}>Extra</th>
                    <th className={th}>Who the extra are</th>
                  </tr>
                </thead>
                <tbody>
                  {d.meals.map((m) => (
                    <tr key={m} className="border-b border-line">
                      <td className={`${td} whitespace-nowrap`}>{mealName(m)}</td>
                      <td className={td}>
                        <span className="flex flex-wrap gap-x-4 gap-y-1">
                          {view.audiences.map((a) => (
                            <label key={a.key} className="inline-flex gap-2 items-center">
                              <input
                                type="checkbox"
                                className={check}
                                checked={aud(d.date, m).includes(a.key)}
                                onChange={() => toggle(d.date, m, a.key)}
                              />
                              {a.name}
                            </label>
                          ))}
                        </span>
                      </td>
                      <td className={td}>
                        <input
                          className={`${smallInput} w-[72px]`}
                          inputMode="numeric"
                          aria-label={`Extra people for ${mealName(m)}, ${weekDay(d.date)}`}
                          value={st.extra[d.date]?.[m]?.count || ''}
                          onChange={(e) => setExtra(d.date, m, 'count', e.target.value)}
                        />
                      </td>
                      <td className={td}>
                        <input
                          className={`${smallInput} w-full`}
                          aria-label={`Who the extra are for ${mealName(m)}, ${weekDay(d.date)}`}
                          placeholder="e.g. partner delegates"
                          value={st.extra[d.date]?.[m]?.note ?? ''}
                          onChange={(e) => setExtra(d.date, m, 'note', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.meals.includes('dinner') ? (
              <label className="block mt-4">
                <span className={fieldLabel}>Dinner choices</span>
                <input
                  className={inputClass}
                  value={choices[d.date] ?? ''}
                  onChange={(e) => setChoices((c) => ({ ...c, [d.date]: e.target.value }))}
                />
                <span className="block text-small text-ink-3 mt-1">
                  Plated or pre-ordered main courses, separated by commas.
                </span>
              </label>
            ) : null}
          </div>
        ))}
      </div>
    </Modal>
  );
}

function RunLineModal({
  eventId,
  view,
  onClose,
  onSaved,
}: {
  eventId: string;
  view: CateringView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const lines = view.settings.runSheet;
  const [f, setF] = useState<RunSheetLine>({
    date: view.days[0]?.date ?? '',
    time: '',
    what: '',
    room: null,
    leftovers: null,
  });
  const save = useMutation(
    async (next: RunSheetLine[]) =>
      (await api.put(`/events/${eventId}/catering/settings`, { runSheet: next })).data.data,
    { onSuccess: onSaved },
  );
  const ready = Boolean(f.time && f.what.trim());
  return (
    <Modal
      title="Add a line to the run sheet"
      why="What the caterer puts out, when and where. The run sheet goes in the caterer’s workbook."
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" disabled={!ready || save.isLoading} onClick={() => save.mutate([...lines, f])}>
            Add the line
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {!ready ? (
            <span className="text-small text-ink-3">Add turns on once there is a time and what goes out.</span>
          ) : null}
        </>
      }
    >
      {save.error ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
        <label className="block">
          <span className={fieldLabel}>Day</span>
          <select className={inputClass} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })}>
            {view.days.map((d) => (
              <option key={d.date} value={d.date}>
                {weekDay(d.date)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={fieldLabel}>Time</span>
          <input
            type="time"
            className={inputClass}
            value={f.time}
            onChange={(e) => setF({ ...f, time: e.target.value })}
          />
        </label>
        <label className="block md:col-span-2">
          <span className={fieldLabel}>What goes out</span>
          <input className={inputClass} value={f.what} onChange={(e) => setF({ ...f, what: e.target.value })} />
        </label>
        <label className="block">
          <span className={fieldLabel}>Room</span>
          <input
            className={inputClass}
            value={f.room ?? ''}
            onChange={(e) => setF({ ...f, room: e.target.value || null })}
          />
        </label>
        <label className="block">
          <span className={fieldLabel}>Leftovers</span>
          <input
            className={inputClass}
            value={f.leftovers ?? ''}
            onChange={(e) => setF({ ...f, leftovers: e.target.value || null })}
          />
        </label>
      </div>
    </Modal>
  );
}

function RunSheet({
  eventId,
  view,
  canEdit,
  onSaved,
}: {
  eventId: string;
  view: CateringView;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const lines = view.settings.runSheet;
  const [adding, setAdding] = useState(false);
  const save = useMutation(
    async (next: RunSheetLine[]) =>
      (await api.put(`/events/${eventId}/catering/settings`, { runSheet: next })).data.data,
    { onSuccess: onSaved },
  );
  const modal = adding ? (
    <RunLineModal
      eventId={eventId}
      view={view}
      onClose={() => setAdding(false)}
      onSaved={() => {
        setAdding(false);
        onSaved();
      }}
    />
  ) : null;
  if (!lines.length)
    return canEdit ? (
      <>
        <AddLink label="Caterer’s run sheet" onClick={() => setAdding(true)} />
        {modal}
      </>
    ) : null;
  return (
    <Panel>
      <PanelHeader title="Caterer’s run sheet" note={`${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`}>
        {canEdit ? <Button onClick={() => setAdding(true)}>Add a line</Button> : null}
      </PanelHeader>
      <PanelBody>
        {save.error ? (
          <div className="mb-3">
            <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
          </div>
        ) : null}
        <table className={stackTable}>
          <thead className="max-md:hidden">
            <tr className={headRow}>
              <th className={th}>Day</th>
              <th className={th}>Time</th>
              <th className={th}>What goes out</th>
              <th className={th}>Room</th>
              <th className={th}>Leftovers</th>
              {canEdit ? (
                <th className={th}>
                  <span className="sr-only">Actions</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="max-md:block">
            {lines.map((l, i) => (
              <tr key={i} className={stackRow}>
                <td className={`${stackCell} whitespace-nowrap max-md:font-semibold`}>
                  {weekDay(l.date)}
                  <span className="md:hidden">, {l.time}</span>
                </td>
                <td className={`${stackCell} max-md:hidden`}>{l.time}</td>
                <td className={stackCell} data-label="What goes out">
                  <Col>What goes out</Col>
                  {l.what}
                </td>
                <td className={stackCell} data-label="Room">
                  <Col>Room</Col>
                  {l.room || dash}
                </td>
                <td className={stackCell} data-label="Leftovers">
                  <Col>Leftovers</Col>
                  {l.leftovers || dash}
                </td>
                {canEdit ? (
                  <td className={`${stackCell} text-right max-md:text-left max-md:pt-2`}>
                    <MoreMenu
                      label={`More for ${l.what}`}
                      items={[
                        {
                          label: 'Remove',
                          destructive: true,
                          disabled: save.isLoading,
                          onClick: () => save.mutate(lines.filter((_, j) => j !== i)),
                        },
                      ]}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </PanelBody>
      {modal}
    </Panel>
  );
}

function DinnerCell({
  eventId,
  person,
  date,
  choices,
}: {
  eventId: string;
  person: CateringPerson;
  date: string;
  choices: string[];
}) {
  const queryClient = useQueryClient();
  const d: Dinner = person.dinners[date] ?? {};
  const [choice, setChoice] = useState(d.choice ?? '');
  const save = useMutation(
    async (patch: Dinner | null) =>
      (await api.patch(`/events/invitees/${person.inviteeId}/dinners`, { dinners: { [date]: patch } })).data.data,
    {
      onSuccess: () => queryClient.invalidateQueries(['event-catering', eventId]),
    },
  );
  return (
    <span className="inline-flex flex-wrap gap-2 items-center">
      <select
        className={smallInput}
        aria-label={`${person.name} at dinner on ${weekDay(date)}`}
        value={d.attending === false ? 'no' : 'yes'}
        onChange={(e) => save.mutate({ ...d, attending: e.target.value === 'yes' })}
      >
        <option value="yes">Attending</option>
        <option value="no">Not attending</option>
      </select>
      <datalist id={`choices-${date}`}>
        {choices.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <input
        list={`choices-${date}`}
        className={`${smallInput} w-[160px]`}
        aria-label={`${person.name}’s main course on ${weekDay(date)}`}
        placeholder="Main course"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        onBlur={() => (choice !== (d.choice ?? '') ? save.mutate({ ...d, choice: choice || undefined }) : undefined)}
        disabled={d.attending === false}
      />
    </span>
  );
}

/** One row per meal across the days; the lines under a meal name say who eats it and what is added. */
function MealsTable({ view }: { view: CateringView }) {
  const audName = (k: string) => view.audiences.find((a) => a.key === k)?.name ?? k;
  const rows = view.meals.filter((m) => view.plan.some((d) => d.meals.some((x) => x.key === m.key)));
  const multi = view.plan.length > 1;
  const notes = (key: string) => {
    const per = view.plan
      .map((d) => {
        const m = d.meals.find((x) => x.key === key);
        if (!m) return null;
        const lines = [
          m.audiences ? `for ${m.audiences.map(audName).join(', ')}` : null,
          m.extra ? `includes ${m.extra.count} extra${m.extra.note ? ` (${m.extra.note})` : ''}` : null,
          m.partnerGuests ? `incl. ${m.partnerGuests} partner guests` : null,
        ].filter((x): x is string => Boolean(x));
        return { date: d.date, lines };
      })
      .filter((x): x is { date: string; lines: string[] } => Boolean(x));
    const all = new Set(per.flatMap((p) => p.lines));
    // The same on every day it is served: say it once.
    const shared = [...all].filter((l) => per.every((p) => p.lines.includes(l)));
    const own = per.flatMap((p) =>
      p.lines.filter((l) => !shared.includes(l)).map((l) => (multi ? `${weekDay(p.date)}: ${l}` : l)),
    );
    return [...shared, ...own];
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full tabular">
        <thead>
          <tr className={headRow}>
            <th className={th}>Meal</th>
            {view.plan.map((d) => (
              <th key={d.date} className={`${th} ${num} whitespace-nowrap`}>
                {weekDay(d.date)}
                {d.label ? <span className="block font-normal text-ink-3">{d.label}</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.key} className="border-b border-line">
              <td className={td}>
                {m.label}
                {notes(m.key).map((l) => (
                  <span key={l} className={em}>
                    {l}
                  </span>
                ))}
              </td>
              {view.plan.map((d) => {
                const c = d.meals.find((x) => x.key === m.key);
                return (
                  <td key={d.date} className={`${td} ${num}`}>
                    {c ? c.count : dash}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DietaryTable({ view, people }: { view: CateringView; people: number }) {
  const days = view.plan;
  const tags = [...new Set(days.flatMap((d) => d.dietary.map((g) => g.tag)))];
  const count = (d: DayPlan, tag: string) => d.dietary.find((g) => g.tag === tag)?.count ?? 0;
  const total = (tag: string) => Math.max(...days.map((d) => count(d, tag)));
  tags.sort((a, b) => total(b) - total(a) || a.localeCompare(b));
  if (!tags.length) return <p className="text-[15px] text-ink-2">No dietary needs given yet.</p>;
  const same = days.every((d) => tags.every((t) => count(d, t) === count(days[0], t)));
  const head =
    days.length === 1
      ? `Dietary need, ${weekDay(days[0].date)}`
      : days.length === 2
        ? 'Dietary need, both days'
        : 'Dietary need, every day';
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full tabular">
          <thead>
            <tr className={headRow}>
              <th className={th}>{same ? head : 'Dietary need'}</th>
              {same ? (
                <th className={`${th} ${num}`}>People</th>
              ) : (
                days.map((d) => (
                  <th key={d.date} className={`${th} ${num} whitespace-nowrap`}>
                    {weekDay(d.date)}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {tags.map((t) => (
              <tr key={t} className="border-b border-line">
                <td className={td}>{t}</td>
                {same ? (
                  <td className={`${td} ${num}`}>{count(days[0], t)}</td>
                ) : (
                  days.map((d) => (
                    <td key={d.date} className={`${td} ${num}`}>
                      {count(d, t) || dash}
                    </td>
                  ))
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-small text-ink-3 mt-3">
        {people} {people === 1 ? 'person' : 'people'}
        {same ? '' : ` (${days.map((d) => `${d.withDietary} on ${weekDay(d.date)}`).join(', ')})`}. Someone can have
        several needs, so the rows add up to more.
      </p>
    </>
  );
}

function leadSentence(v: CateringView, dietary: number) {
  const days = v.plan;
  if (!days.length) return 'No event days yet, so there are no meals to count.';
  const eat = days.map((d, i) =>
    i === 0
      ? `${d.attendees} ${d.attendees === 1 ? 'person eats' : 'people eat'} on ${weekDay(d.date)}`
      : `${d.attendees} on ${weekDay(d.date)}`,
  );
  const changes = v.changeCounts.added + v.changeCounts.changed + v.changeCounts.removed;
  const status = !v.lastSent
    ? 'the caterer hasn’t had a list yet'
    : changes
      ? `${changes} ${changes === 1 ? 'change' : 'changes'} since the caterer’s last list`
      : 'the caterer’s list is up to date';
  const diet = dietary
    ? `${dietary} ${dietary === 1 ? 'has' : 'have'} dietary needs`
    : 'Nobody has given a dietary need';
  return `${list(eat)}. ${diet}, and ${status}.`;
}

export default function CateringPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const catering = useQuery<CateringView>(
    ['event-catering', eventId],
    async () => (await api.get(`/events/${eventId}/catering`)).data.data,
  );
  const [editMeals, setEditMeals] = useState(false);
  const [editAud, setEditAud] = useState(false);
  const [all, setAll] = useState(false);
  const refresh = () => queryClient.invalidateQueries(['event-catering', eventId]);
  if (catering.isLoading) return <SkeletonRows rows={6} />;
  if (catering.error || !catering.data)
    return <ErrorNotice>{errorMessage(catering.error, 'Could not load catering.')}</ErrorNotice>;
  const v = catering.data;
  const dinnerDays = v.plan.filter((d) => d.dinner);
  const today = new Date().toISOString().slice(0, 10);
  const withDietary = (v.people ?? []).filter((p) => p.dietary);
  const dietaryCount = v.people ? withDietary.length : Math.max(0, ...v.plan.map((d) => d.withDietary));
  const changes = v.changeCounts.added + v.changeCounts.changed + v.changeCounts.removed;
  const kosherDays = v.plan.filter((d) => d.kosher);
  const kosherCount = Math.max(0, ...kosherDays.map((d) => d.kosher?.count ?? 0));
  const noticeBy = kosherDays.map((d) => d.kosher?.noticeBy ?? '').sort()[0];
  const kosherLate = Boolean(noticeBy && today > noticeBy);
  const placeCards = async (date: string) => {
    const tab = openBlankTab();
    try {
      showPrintable(tab, (await api.get(`/events/${eventId}/dayof/place-cards?date=${date}`)).data.data.html);
    } catch {
      tab?.close();
    }
  };
  // On a dinner day everyone in person is listed, for the dinner columns; otherwise only people with a need.
  const people = (v.people ?? []).filter((p) => p.dietary || dinnerDays.length);
  const shown = all ? people : people.slice(0, PEOPLE_SHOWN);
  return (
    <div>
      <Lead
        lead={leadSentence(v, dietaryCount)}
        sub="Meal numbers per day, the list you send the caterer, and who needs what. Counts are registered and confirmed invitees attending in person that day."
      />
      <Panel>
        <PanelHeader title="Meals and numbers" note={`${v.inPerson} in person`}>
          {canEdit ? <Button onClick={() => setEditMeals(true)}>Change meals</Button> : null}
          {canEdit ? <Button onClick={() => setEditAud(true)}>Who eats what, extra headcounts</Button> : null}
        </PanelHeader>
        <PanelBody>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-12 gap-y-8">
            <div>
              <MealsTable view={v} />
              {noticeBy && (kosherCount || !kosherLate) ? (
                kosherCount && kosherLate ? (
                  <div className="mt-3">
                    <ErrorNotice>
                      {kosherCount} kosher {kosherCount === 1 ? 'meal' : 'meals'}: the two weeks’ notice ended{' '}
                      {monthDay(noticeBy)}. Tell the caterer now. Halal is standard.
                    </ErrorNotice>
                  </div>
                ) : (
                  <p className="text-[15px] leading-[22px] text-ink-2 mt-3">
                    {kosherCount ? `${kosherCount} kosher ${kosherCount === 1 ? 'meal' : 'meals'}. ` : ''}Kosher meals
                    need two weeks’ notice: tell the caterer by {weekDay(noticeBy)}. Halal is standard.
                  </p>
                )
              ) : null}
              {dinnerDays.map((d) =>
                d.dinner ? (
                  <p key={d.date} className="text-[15px] leading-[22px] text-ink mt-3">
                    Dinner {weekDay(d.date)}: {d.dinner.count}
                    {d.dinner.declined ? ` (${d.dinner.declined} not attending)` : ''}
                    {d.dinner.choices.length
                      ? ` · ${d.dinner.choices.map((c) => `${c.choice} ${c.count}`).join(' · ')}${d.dinner.noChoice ? ` · no choice yet ${d.dinner.noChoice}` : ''}`
                      : ''}
                  </p>
                ) : null,
              )}
            </div>
            <div>
              <DietaryTable view={v} people={dietaryCount} />
            </div>
          </div>
        </PanelBody>
      </Panel>
      {!v.peopleVisible ? (
        <div className="mt-8">
          <InfoNotice>
            Names and dietary details are for admins.{' '}
            {v.lastSent
              ? `Numbers last sent ${when(v.lastSent.sentAt)}; ${changes} ${changes === 1 ? 'change' : 'changes'} since.`
              : 'Numbers not sent yet.'}
          </InfoNotice>
        </div>
      ) : (
        <>
          <Panel>
            <PanelHeader
              title="List for the caterer"
              note={sentNote(v.lastSent?.sentAt ?? null, v.changes?.sentBefore ? changes : 0, when)}
            />
            <PanelBody>
              {canEdit ? (
                <div className="max-w-[820px]">
                  <SendSteps
                    download={`/events/${eventId}/catering/export`}
                    markSent={`/events/${eventId}/catering/mark-sent`}
                    to={v.caterer ?? 'the caterer'}
                    what="catering numbers, dietary list and dinner lists"
                    lastSent={v.lastSent?.sentAt ?? null}
                    onSent={refresh}
                    downloadLabel="Download the list"
                  />
                </div>
              ) : null}
              {v.changes ? <ChangesTable changes={v.changes} labels={FIELD_LABELS} /> : null}
            </PanelBody>
          </Panel>
          <RunSheet eventId={eventId} view={v} canEdit={canEdit} onSaved={refresh} />
          <Panel>
            <PanelHeader
              title={dinnerDays.length ? 'Dietary needs and dinners' : 'People with dietary needs'}
              note={
                dinnerDays.length
                  ? `${withDietary.length} with dietary needs · ${people.length} in person`
                  : `${withDietary.length}`
              }
            >
              {dinnerDays.map((d) => (
                <Button key={d.date} size="sm" onClick={() => placeCards(d.date)}>
                  Place cards {weekDay(d.date)}
                </Button>
              ))}
            </PanelHeader>
            <PanelBody>
              {!people.length ? (
                <EmptyState title={(v.people ?? []).length ? 'No dietary needs given yet.' : 'Nobody in person yet.'}>
                  {(v.people ?? []).length
                    ? 'Needs come from the registration form, or from the person on People.'
                    : 'People appear here once they register to attend in person.'}
                </EmptyState>
              ) : (
                <table className={stackTable}>
                  <thead className="max-md:hidden">
                    <tr className={headRow}>
                      <th className={`${th} w-[26%]`}>Name</th>
                      <th className={th}>What they wrote</th>
                      <th className={th}>Counted as</th>
                      {dinnerDays.map((d) => (
                        <th key={d.date} className={th}>
                          Dinner {weekDay(d.date)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="max-md:block">
                    {shown.map((p) => (
                      <tr key={p.inviteeId} className={stackRow}>
                        <td className={`${stackCell} font-semibold`}>{p.name}</td>
                        <td className={`${stackCell} text-ink-2`} data-label="What they wrote">
                          <Col>Wrote</Col>
                          {p.dietary ? `“${p.dietary}”` : dash}
                        </td>
                        <td className={stackCell} data-label="Counted as">
                          <Col>Counted as</Col>
                          {p.tags.length ? sentence(p.tags.join(', ')) : dash}
                        </td>
                        {dinnerDays.map((d) => (
                          <td key={d.date} className={stackCell} data-label={`Dinner ${weekDay(d.date)}`}>
                            <Col>Dinner {weekDay(d.date)}</Col>
                            {!p.days.includes(d.date) ? (
                              dash
                            ) : canEdit ? (
                              <DinnerCell eventId={eventId} person={p} date={d.date} choices={d.dinnerChoices ?? []} />
                            ) : p.dinners[d.date]?.attending === false ? (
                              'Not attending'
                            ) : (
                              (p.dinners[d.date]?.choice ?? 'Attending')
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-[15px] leading-[22px] text-ink-3 mt-3 max-w-[72ch]">
                {people.length > PEOPLE_SHOWN && !all ? (
                  <>
                    <button
                      type="button"
                      className="text-accent underline underline-offset-[3px] hover:text-ink"
                      onClick={() => setAll(true)}
                    >
                      Show all {people.length}
                    </button>
                    {' · '}
                  </>
                ) : null}
                Change a need on the person, on People. Dinners: tick “Dinner” on a day in the event settings to get a
                dinner list.
              </p>
            </PanelBody>
          </Panel>
        </>
      )}
      {editMeals ? (
        <MealsModal
          eventId={eventId}
          view={v}
          onClose={() => setEditMeals(false)}
          onSaved={() => {
            setEditMeals(false);
            refresh();
          }}
        />
      ) : null}
      {editAud ? (
        <AudiencesModal
          eventId={eventId}
          view={v}
          onClose={() => setEditAud(false)}
          onSaved={() => {
            setEditAud(false);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
